import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import api from "@/lib/axios"
import { getListKomponenPenilaian } from "@/services/getData"
import { BobotKomponenPenilaian, KomponenPenilaian, Option } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: BobotKomponenPenilaian) => {
  const response = await api.post(
    "/operator-pasca/komponen-penilaian/bobot-komponen-store",
    newData
  )
  return response.data
}

const patchData = async (newData: BobotKomponenPenilaian) => {
  const response = await api.patch(
    "/operator-pasca/komponen-penilaian/bobot-komponen-update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_bobot_komponen_penilaian_pasca: string
}) => {
  const response = await api({
    url: "/operator-pasca/komponen-penilaian/bobot-komponen-delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: BobotKomponenPenilaian | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<BobotKomponenPenilaian>({
    id_ref_bobot_komponen_penilaian_pasca:
      selectedRow?.id_ref_bobot_komponen_penilaian_pasca || "",
    id_ref_komponen_penilaian_pasca:
      selectedRow?.id_ref_bobot_komponen_penilaian_pasca || "",
    kriteria: selectedRow?.kriteria || "",
    bobot: selectedRow?.bobot || 0,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: listKomponenPenilaian } = useQuery({
    queryKey: ["listKomponenPenilaian"],
    queryFn: getListKomponenPenilaian,
    select: (data) => {
      return data?.map((item: KomponenPenilaian) => {
        return {
          label: item.nama_komponen,
          value: `${item.id_ref_komponen_penilaian_pasca}`,
        }
      })
    },
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listBobotKomponenPenilaian"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete "
          description={`Apakah anda yakin akan menghapus "${""}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data" : "Update Data"}
      />
      <div>
        <Label>Komponen Penilaian</Label>
        <Select
          value={form.id_ref_komponen_penilaian_pasca || ""}
          onValueChange={(value: string) => {
            setForm((prev) => ({
              ...prev,
              id_ref_komponen_penilaian_pasca: `${value}`,
            }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Parent Komponen"}>
              {form.id_ref_komponen_penilaian_pasca &&
                listKomponenPenilaian?.find((option: Option) => {
                  return (
                    option.value == form.id_ref_bobot_komponen_penilaian_pasca
                  )
                })?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {listKomponenPenilaian?.map((option: Option, index: number) => (
              <SelectItem key={index} value={`${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Kriteria</Label>
        <Input
          placeholder="Ketik kriteria..."
          value={form.kriteria}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, kriteria: event.target.value }))
          }}
        />
      </div>
      <div>
        <Label>Bobot</Label>
        <Input
          type="number"
          placeholder="Ketik bobot..."
          value={form.bobot}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, bobot: Number(event.target.value) }))
          }}
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
